import React, { useCallback, useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';

import useAlert from '../../Alerts/useAlert';
import CentredSpinner from '../../Components/CentredSpinner';
import NoFilter from '../../DataTable/datatable.nofilter';
import SelectFilter from '../../DataTable/datatable.selectfilter';
import EditableDatatable from '../../DataTable/Editable/datatable.editable';
import EditableColumn from '../../DataTable/Editable/datatable.editable.inputcolumn';
import RecipeEditDialog from '../../Dialogs/Configuration/RecipeEditDialog';
import { Changes } from '../../Types/Changes';
import { RecipeData } from '../../Types/RecipeData';
import WithComment from '../../Types/WithComment';
import { useBusinessUnits } from '../../Utilities/useBusinessUnits';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import CommonConfigPage from './CommonConfigPage';


interface RecipeDataTableProps {
    showEditDialog: Function,
    includeDeleted: boolean,
    refresh: any,
}

function RecipeDataTable({ showEditDialog, includeDeleted, refresh }: RecipeDataTableProps) {
    const { fetchDataAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    const [tableData, setTableData] = useState <RecipeData[]>([]);

    const getRecipeData = useCallback(async () => {
        let url = `api/v1/recipedata/?includeDeleted=${includeDeleted}`;
        setLoaded(false);
        await fetchDataAuthenticated(url)
            .then(response => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error('Something went wrong.');
            })
            .then(data => {
                setTableData(data);
                setErrored(false);
            })
            .catch(error => {
                setErrored(true);
            })
            .finally(() => {
                setLoaded(true);
            });
    }, [fetchDataAuthenticated, includeDeleted]);

    useEffect(() => {
        getRecipeData();
    }, [refresh, getRecipeData]);

    const getRecipeForOnRowClick = async (recipeData:any) => {
        if (!recipeData.recipeId) {
            return recipeData;
        }
        let url = `api/v1/recipe/${recipeData.recipeId}`;
        let recipe: any | null = { recipeId: recipeData.recipeId };
        await fetchDataAuthenticated(url)
            .then(response => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error('Something went wrong.');
            })
            .then(data => {
                recipe = data;
            })
            .catch(error => {
                recipe = null;
            });

        return showEditDialog(recipe);
    }

    const tableColumns = [
        {
            Header: 'Recipe Name',
            accessor: 'recipeName',
            filter: 'includesString',
            Cell: (props:any) => {
                return (<Nav><Nav.Link onClick={async () => await getRecipeForOnRowClick(props.row.original)}>{props.value}</Nav.Link></Nav>)
            }
        },
        {
            Header: 'Quality Name',
            accessor: 'qualityName',
            filter: 'includesString',
        },
        {
            Header: 'Analysis',
            accessor: 'analysis',
            filter: 'includesString',
        },
        {
            Header: 'Component',
            accessor: 'component',
            filter: 'includesString',
        },
        {
            Header: 'Recipe Property Name',
            accessor: 'recipePropertyName',
            Filter: SelectFilter,
            filter: 'includesString',
        },
        {
            Header: 'Recipe Data Type Name',
            accessor: 'recipeDataTypeName',
            Filter: SelectFilter,
            filter: 'includesString',
        },
        EditableColumn('Min Value', 'minValue', true, undefined, NoFilter),
        EditableColumn('Target Value', 'targetValue', true, undefined, NoFilter),
        EditableColumn('Max Value', 'maxValue', true, undefined, NoFilter),
        {
            Header: 'Business Unit',
            accessor: 'businessUnitName',
            Filter: SelectFilter,
            filter: 'equals'
        }
    ];

    const getRowProps = (row:any) => {
        if (row.original.isDeleted)
            return { className: "deleted" };

        return {};
    };

    const initialState = {
        sortBy: [
            {
                id: 'name',
                desc: false
            }
        ]
    };

    const saveChanges = async (data: Changes<RecipeData>, comment: string) => {
        let url = `/api/v1/recipeData/update`;
        let body: WithComment<Changes<RecipeData>> = {
            data: data,
            comment: comment,
        };

        data.updates.forEach(recipeData => {
            if (recipeData.minValue === "") {
                recipeData.minValue = null;
            }
            if (recipeData.maxValue === "") {
                recipeData.maxValue = null;
            }
            if (recipeData.targetValue === "") {
                recipeData.targetValue = null;
            }
        });

        await fetchDataAuthenticated(url, 'PUT', JSON.stringify(body))
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", `Updated the recipe data`);
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to update the recipe data:" + errorMessage);
                    throw new Error("Failed to update the recipe data.");
                }
                getRecipeData();
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
    }


    return (loaded
        ? <EditableDatatable identifier={'recipeDataId'} tableColumns={tableColumns} initialTableData={tableData} initialState={initialState} getRowProps={getRowProps} saveChanges={saveChanges} />
        : errored
            ? <p>There was a problem.</p>
            : <CentredSpinner />
    );
}

function RecipeDataConfiguration() {
    const { businessUnits } = useBusinessUnits(false);

    return (
        <CommonConfigPage
            title="Recipe Data"
            Table={RecipeDataTable}
            Dialog={RecipeEditDialog}
            businessUnits={businessUnits}
        />
    );
}

export default RecipeDataConfiguration;